import { useSyncExternalStore } from 'react';

export const IMAGE_REVISION_STORAGE_KEY = 'kjauto.imageRevisions';
const TTL_MS = 48 * 60 * 60 * 1000;

type StoredEntry = {
  revision: number;
  expiresAt: number;
};

let entries = new Map<string, StoredEntry>();
const listeners = new Set<() => void>();

const isStoredEntry = (value: unknown): value is StoredEntry => {
  if (!value || typeof value !== 'object') return false;
  const record = value as { revision?: unknown; expiresAt?: unknown };
  return typeof record.revision === 'number' && typeof record.expiresAt === 'number';
};

export const loadImageRevisionsFromStorage = () => {
  entries = new Map();
  try {
    const raw = localStorage.getItem(IMAGE_REVISION_STORAGE_KEY);
    if (!raw) return;

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;

    const now = Date.now();
    Object.entries(parsed as Record<string, unknown>).forEach(([id, value]) => {
      if (!isStoredEntry(value) || value.expiresAt <= now) return;
      entries.set(id, value);
    });
  } catch {
    entries = new Map();
  }
};

const persist = () => {
  try {
    const payload: Record<string, StoredEntry> = {};
    entries.forEach((entry, id) => {
      payload[id] = entry;
    });
    localStorage.setItem(IMAGE_REVISION_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage can be unavailable in private mode.
  }
};

const notify = () => {
  listeners.forEach(listener => listener());
};

export const clearImageRevisions = () => {
  entries = new Map();
  try {
    localStorage.removeItem(IMAGE_REVISION_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
  notify();
};

export const bumpImageRevision = (googleFileId: string) => {
  const now = Date.now();
  const previous = entries.get(googleFileId)?.revision ?? 0;
  const revision = now > previous ? now : previous + 1;
  entries.set(googleFileId, { revision, expiresAt: now + TTL_MS });
  persist();
  notify();
  return revision;
};

export const getImageRevision = (googleFileId?: string | null) => {
  if (!googleFileId) return undefined;
  const entry = entries.get(googleFileId);
  if (!entry || entry.expiresAt <= Date.now()) return undefined;
  return entry.revision;
};

export const subscribeImageRevisions = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useImageRevision = (googleFileId?: string | null) =>
  useSyncExternalStore(
    subscribeImageRevisions,
    () => getImageRevision(googleFileId),
    () => undefined
  );

export const googleFileIdFromImageRequest = (url?: string) => {
  if (!url) return undefined;
  const path = url.split('?')[0];
  const match = /\/images\/([^/]+)$/.exec(path);
  if (!match?.[1]) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

export const imageRevisionParam = (method: string | undefined, url?: string) => {
  if ((method ?? 'get').toLowerCase() !== 'get') return undefined;
  return getImageRevision(googleFileIdFromImageRequest(url));
};

loadImageRevisionsFromStorage();
